import { LiveAnnouncer } from "@angular/cdk/a11y";
import {
  ChangeDetectionStrategy,
  Component,
  DOCUMENT,
  DestroyRef,
  ElementRef,
  Injector,
  Renderer2,
  afterNextRender,
  computed,
  effect,
  inject,
  signal,
  untracked,
  viewChild
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { Router } from "@angular/router";
import {
  NOTIFICATION_TRAY_ENABLED,
  NotificationService,
  VpFloatingToast,
  VpNotification
} from "@vp/shared/notification-service";
import { AppStoreService } from "@vp/shared/store/app";
import {
  VpActionBtnComponent,
  VpCountPillComponent,
  VpDialogShellComponent,
  VpIconBtnComponent,
  VpIconComponent,
  VpToastComponent
} from "@vp/shared/ui/primitives";
import { NotificationTrayEffects } from "./notification-tray.effects";
import {
  VpNotificationMetaComponent,
  isLockedNotification,
  notificationKicker
} from "./vp-notification-meta.component";

/**
 * Renders the notification store: the floating stack, the tray trigger and the
 * non-modal tray panel, pinned bottom-left over the app. Mounted once in the app
 * shell. The store owns behavior; this component renders its state and forwards
 * user intent.
 */
@Component({
  selector: "vp-notification-host",
  imports: [
    VpToastComponent,
    VpIconComponent,
    VpIconBtnComponent,
    VpActionBtnComponent,
    VpCountPillComponent,
    VpDialogShellComponent,
    VpNotificationMetaComponent
  ],
  templateUrl: "./vp-notification-host.component.html",
  styleUrls: ["./vp-notification-host.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    // Scoped to the host so closing a dialog or dropdown with Esc never tucks toasts.
    "(keydown.escape)": "onEscape($event)"
  }
})
export class VpNotificationHostComponent {
  protected readonly store = inject(NotificationService);
  private readonly effects = inject(NotificationTrayEffects);
  private readonly router = inject(Router);
  private readonly announcer = inject(LiveAnnouncer);
  private readonly renderer = inject(Renderer2);
  private readonly document = inject(DOCUMENT);
  private readonly injector = inject(Injector);
  private readonly hostElement: HTMLElement = inject(ElementRef).nativeElement;

  // `loggedInUser$`, not `user$`: the tray has to go away on logout, and `user$` never emits null.
  private readonly user = toSignal(inject(AppStoreService).loggedInUser$, { initialValue: null });

  /** Off, only the floating stack renders: no trigger, no panel, nothing tuckable. */
  protected readonly trayEnabled = inject(NOTIFICATION_TRAY_ENABLED);

  protected readonly signedIn = computed(() => this.user() !== null);
  protected readonly trayOpen = signal(false);

  /** Stays while the tray is open, even when the last item has just been dismissed. */
  protected readonly showTrigger = computed(
    () =>
      this.trayEnabled() &&
      this.signedIn() &&
      (this.trayOpen() || this.store.trayCount() > 0 || this.store.undoCount() !== null)
  );

  protected readonly triggerLabel = computed(() => {
    const urgent = this.store.urgentCount();
    return `Notifications, ${this.store.trayCount()} active${urgent > 0 ? `, ${urgent} urgent` : ""}`;
  });

  /**
   * Top-down DOM order for a stack that reads bottom-up from the trigger: non-urgent
   * entries sit next to it with urgent ones above. Each group stays oldest first, so
   * the newest lands nearest the trigger. Grouped on `urgent` rather than locked, so a
   * light that clears keeps its place while it shows "Cleared".
   */
  protected readonly stackEntries = computed(() => {
    const floating = this.store.floating();
    return [
      ...floating.filter(entry => entry.notification.urgent),
      ...floating.filter(entry => !entry.notification.urgent)
    ];
  });

  private readonly trigger = viewChild<ElementRef<HTMLButtonElement>>("trigger");
  private readonly stack = viewChild<ElementRef<HTMLElement>>("stack");

  private removeOutsideClick: (() => void) | null = null;
  private readonly announcedIds = new Set<string>();
  /** `undefined` until the first run, so the initial state is treated as a user change. */
  private announcedForUser: string | null | undefined = undefined;

  constructor() {
    this.effects.start();

    effect(() => {
      const userId = this.user()?.userId ?? null;
      const floating = this.store.floating();
      untracked(() => this.announceNew(userId, floating));
    });

    // Signing out removes the panel; the stack mustn't stay hidden behind a tray that's gone.
    effect(() => {
      if (!this.signedIn()) {
        untracked(() => this.hideTray());
      }
    });

    inject(DestroyRef).onDestroy(() => this.stopOutsideClick());
  }

  protected isLocked(n: VpNotification): boolean {
    return isLockedNotification(n);
  }

  protected isNavigable(n: VpNotification): boolean {
    return n.kind === "call-light" && !!n.sourcePath;
  }

  protected openLabel(n: VpNotification): string {
    const kicker = notificationKicker(n);
    return `${kicker ? `${kicker}: ` : ""}${n.heading}. Open device`;
  }

  protected onTriggerClick(event: MouseEvent): void {
    if (this.trayOpen()) {
      this.close();
    } else {
      this.openTray(event.detail === 0);
    }
  }

  /** `viaKeyboard` comes from `MouseEvent.detail === 0`, the click a key press synthesizes. */
  protected openTray(viaKeyboard: boolean): void {
    // No tray, or no user: nothing to show, and no session for the reconcile lookups.
    if (!this.trayEnabled() || !this.signedIn()) {
      return;
    }
    this.store.tuckAll();
    this.effects.reconcile({ throttled: true });
    if (!this.trayOpen()) {
      this.trayOpen.set(true);
      this.listenForOutsideClick();
    }
    if (viaKeyboard) {
      // The rows only become focusable once the panel loses `hidden` on the next render.
      afterNextRender(() => this.focusFirstRow(), { injector: this.injector });
    }
  }

  protected close(): void {
    this.hideTray();
    this.trigger()?.nativeElement.focus();
  }

  protected open(n: VpNotification): void {
    this.store.acknowledge(n.id);
    this.close();
    if (!n.sourcePath) {
      return;
    }
    const onSourcePath = this.router.url.split(/[?#]/)[0] === n.sourcePath;
    void this.router.navigate([n.sourcePath], {
      queryParams: { search: n.deviceName },
      // On the device page already, keep its other filters; from anywhere else they'd be
      // another page's params carried over.
      ...(onSourcePath ? { queryParamsHandling: "merge" as const } : {})
    });
  }

  /** Clicking a transient's body dismisses it, as toastr's `tapToDismiss` did. */
  protected onToastClick(entry: VpFloatingToast): void {
    if (entry.notification.kind === "transient") {
      this.store.dismiss(entry.id);
    }
  }

  /** Moving focus between the toast's own controls mustn't restart its timer. */
  protected onToastFocusOut(id: string, event: FocusEvent): void {
    const toast = event.currentTarget as HTMLElement;
    const next = event.relatedTarget;
    if (!(next instanceof Node) || !toast.contains(next)) {
      this.store.resume(id);
    }
  }

  protected onEscape(event: Event): void {
    if (this.trayOpen()) {
      this.close();
      return;
    }
    const stack = this.stack()?.nativeElement;
    if (stack && event.target instanceof Node && stack.contains(event.target)) {
      this.store.tuckAll();
    }
  }

  private focusFirstRow(): void {
    const row = this.hostElement.querySelector<HTMLElement>(".vp-notification-tray__item");
    const target =
      row?.querySelector<HTMLElement>(".vp-toast__open") ??
      row ??
      this.hostElement.querySelector<HTMLElement>(
        'button[data-testid="notification-tray-collapse"]'
      );
    target?.focus();
  }

  /** Hides the panel without moving focus: an outside click has already put it elsewhere. */
  private hideTray(): void {
    this.trayOpen.set(false);
    this.stopOutsideClick();
  }

  private listenForOutsideClick(): void {
    this.stopOutsideClick();
    this.removeOutsideClick = this.renderer.listen(this.document, "click", (event: MouseEvent) => {
      // `composedPath`, not `contains`: a row's ×, Undo and the footer buttons remove the
      // clicked node before this runs, so it's no longer inside the host.
      if (!event.composedPath().includes(this.hostElement)) {
        this.hideTray();
      }
    });
  }

  private stopOutsideClick(): void {
    this.removeOutsideClick?.();
    this.removeOutsideClick = null;
  }

  /**
   * Announces each entry once, when it starts floating. After a user change the current
   * entries are only recorded, so urgent items re-floated from storage aren't read again.
   */
  private announceNew(userId: string | null, floating: readonly VpFloatingToast[]): void {
    const userChanged = userId !== this.announcedForUser;
    this.announcedForUser = userId;

    const floatingIds = new Set(floating.map(entry => entry.id));
    for (const id of this.announcedIds) {
      if (!floatingIds.has(id)) {
        this.announcedIds.delete(id);
      }
    }

    for (const entry of floating) {
      if (this.announcedIds.has(entry.id)) {
        continue;
      }
      this.announcedIds.add(entry.id);
      if (!userChanged) {
        const n = entry.notification;
        void this.announcer.announce(
          this.spokenText(n),
          isLockedNotification(n) ? "assertive" : "polite"
        );
      }
    }
  }

  private spokenText(n: VpNotification): string {
    const message = n.html ? this.toPlainText(n.message) : n.message;
    return [n.heading, message]
      .map(part => part.trim())
      .filter(part => part.length > 0)
      .join(". ");
  }

  private toPlainText(html: string): string {
    // An element of an inert document: parsing never loads images or runs inline handlers.
    const element = this.document.implementation.createHTMLDocument("").createElement("div");
    element.innerHTML = html;
    return element.textContent ?? "";
  }
}
